import { memo, useCallback, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { Icon, Link, Modal, Switch, tokens } from '@hotjar/hotsauce';
import { ModalStateEnum } from '../../types';

const StyledModal = styled(Modal)`
  overflow: hidden;
`;

const ItemListDiv = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${tokens.spacer.x3};
  overflow: auto;
`;

const borderBottomStyle = css`
  border-bottom: 1px solid ${tokens.color.ui_border_light};
`;

const ItemDiv = styled.div<{ hideBorderBottom?: boolean }>`
  display: grid;
  grid-template-columns: auto fit-content(50%);
  grid-template-areas:
    'title       switch'
    'description switch';
  gap: ${tokens.spacer.x0};
  padding: ${tokens.spacer.x2} 0;
  ${({ hideBorderBottom }) => !hideBorderBottom && borderBottomStyle}
`;

const ItemTitle = styled.div`
  grid-area: title;
  ${tokens.text('subtitle3')}
`;

const ItemDescription = styled.div`
  grid-area: description;
  ${tokens.text('caption')}
  color: ${tokens.color.text_light};
`;

const ItemSwitch = styled.div`
  grid-area: switch;
  align-self: center;
`;

const FooterDiv = styled.div`
  background-color: ${tokens.color.neutral_100};
  padding: ${tokens.spacer.x2} ${tokens.spacer.x3};
  ${tokens.text('caption')}
  font-size: 10px;
`;

const StyledSwitch = styled(Switch)`
  margin-bottom: 0;
`;

const StyledIcon = styled(Icon)`
  margin-left: ${tokens.spacer.x0};
  margin-right: 10px;
  color: ${tokens.color.text_light};
`;

interface Props {
  onClose: () => void;
  isOpen: boolean;
  isAutoplaying: boolean;
  onAutoplayToggle: (isChecked: boolean) => void;
  isJumpStartActive: boolean;
  onToggleJumpStart: () => void;
  isMuteClicksActive: boolean;
  onToggleMuteClicks: () => void;
  onOpenKeyboardShorcutModal: () => void;
  isPlaying: boolean;
  onPlay: (showAnimation?: boolean) => void;
  onPause: (showAnimation?: boolean, isManual?: boolean) => void;
  activeModal: ModalStateEnum;
}

const SettingsModalUnMemo = ({
  onClose,
  isOpen,
  isAutoplaying,
  onAutoplayToggle,
  isJumpStartActive,
  onToggleJumpStart,
  isMuteClicksActive,
  onToggleMuteClicks,
  onOpenKeyboardShorcutModal,
  isPlaying,
  onPause,
  onPlay,
  activeModal,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [wasPlaying, setWasPlaying] = useState(false);

  const pauseRecordingIfNeeded = useCallback(() => {
    setWasPlaying(isPlaying);
    if (isPlaying) {
      onPause();
    }
  }, [isPlaying, onPause]);
  const resetRecordingPlayState = useCallback(() => {
    if (activeModal === ModalStateEnum.CLOSE && wasPlaying) {
      onPlay();
    }
  }, [wasPlaying, onPlay, activeModal]);

  return (
    <StyledModal
      contentLabel="Modal Example"
      isOpen={isOpen}
      onClose={() => onClose()}
      size="m"
      onAfterOpen={pauseRecordingIfNeeded}
      onAfterClose={resetRecordingPlayState}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
    >
      <Modal.Header title={t('common:player.settings-modal.title')} />
      <ItemListDiv>
        <ItemDiv>
          <ItemTitle>{t('common:player.settings-modal.autoplay')}</ItemTitle>
          <ItemDescription>
            {t('common:player.settings-modal.autoplay-description')}
          </ItemDescription>
          <ItemSwitch>
            <StyledSwitch isChecked={isAutoplaying} onChange={onAutoplayToggle} />
          </ItemSwitch>
        </ItemDiv>
        <ItemDiv>
          <ItemTitle>{t('common:player.settings-modal.jump-start')}</ItemTitle>
          <ItemDescription>
            {t('common:player.settings-modal.jump-start-description')}
          </ItemDescription>
          <ItemSwitch>
            <StyledSwitch isChecked={isJumpStartActive} onChange={onToggleJumpStart} />
          </ItemSwitch>
        </ItemDiv>
        <ItemDiv hideBorderBottom>
          <ItemTitle>{t('common:player.settings-modal.mute-clicks')}</ItemTitle>
          <ItemDescription>
            {t('common:player.settings-modal.mute-clicks-description')}
          </ItemDescription>
          <ItemSwitch>
            <StyledSwitch isChecked={isMuteClicksActive} onChange={onToggleMuteClicks} />
          </ItemSwitch>
        </ItemDiv>
      </ItemListDiv>
      <FooterDiv>
        <StyledIcon icon="key_press" />
        <Link as="button" kind="inline" onClick={() => onOpenKeyboardShorcutModal()}>
          {t('common:player.settings-modal.view-keyboard-shortcuts')}
        </Link>
      </FooterDiv>
    </StyledModal>
  );
};

export const SettingsModal = memo(SettingsModalUnMemo);
